import React, { useMemo } from 'react';
import { styled, colors, type SwitchProps, Switch } from '@material-ui/core';

interface StyledToggleProps extends Omit<SwitchProps, 'width' | 'height'> {
  width?: number;
  height?: number;
}

/** A toggle switch designed specifically for the SellersShieldVariant.tsx
 * component, blue in color & oversized by default.
 */
export function StyledBlueToggle({ width = 100, height = 44, ...props }: StyledToggleProps) {
  // Calculate proportional dimensions
  const dimensions = useMemo(() => {
    const margin = Math.max(2, Math.floor(height * 0.045)); // ~4.5% of height
    const thumbSize = height - margin * 2;
    const translateDistance = width - height;
    return { margin, thumbSize, translateDistance };
  }, [width, height]);

  const StyledSwitch = useMemo(
    () =>
      styled(Switch)({
        width,
        height,
        padding: 0,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: dimensions.margin,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: `translateX(${dimensions.translateDistance}px)`,
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#D4E5F7',
              opacity: 1,
              border: 0,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: '#1D6FED',
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#1D6FED',
            border: '6px solid #fff',
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: colors.grey[900],
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
            backgroundColor: colors.grey[600],
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: dimensions.thumbSize,
          height: dimensions.thumbSize,
          backgroundColor: '#505B6F',
        },
        '& .MuiSwitch-track': {
          borderRadius: height / 2,
          backgroundColor: '#DCE1E8',
          opacity: 1,
        },
      }),
    [width, height, dimensions]
  );

  return <StyledSwitch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />;
}
